<template>
  <el-dialog
      width="75%"
      :title="`Update coupon - ${ form.id }`"
      :visible.sync="modalState"
      :close-on-click-modal="true"
      :before-close="hideModalHandler"
      :destroy-on-close="true"
  >
    <div class="form-block">
      <el-form :model="form" :rules="validateRules" ref="updateCouponForm" class="coupon-form" label-position="left">

        <!-- Coupon URL & Code inputs -->
        <el-row class="links-row">
          <div class="links-row-block">

            <div class="links-row-block__item">
              <el-form-item label="URL" class="url-block">
                <el-input v-model="form.link" placeholder="http://example.com/" size="small" disabled />
              </el-form-item>
            </div>

            <div class="links-row-block__item">
              <CopyButton label="URL" :target="form.link" style="margin-top: 20px" />
            </div>
          </div>

          <div class="links-row-block">
            <div class="links-row-block__item">
              <el-form-item label="Code">
                <el-input v-model="form.code" size="small" disabled/>
              </el-form-item>
            </div>
            <div class="links-row-block__item">
              <CopyButton label="Code" :target="form.code" style="margin-top: 20px" />
            </div>
          </div>
        </el-row>

        <!-- Coupon Settings columns -->
        <el-row class="settings-row">
          <div class="settings-row-block">

            <!-- Discount switcher -->
            <div class="settings-row-block__item">
              <el-form-item label="Discount Settings" label-width="125px">
                <el-switch
                    size="small"
                    v-model="form.include_coupon"
                    active-color="#13ce66"
                    inactive-color="#ff4949"
                />
              </el-form-item>
            </div>

            <!-- Coupon active switcher -->
            <div class="settings-row-block__item">
              <el-form-item label="Active" label-width="125px">
                <el-switch
                    size="small"
                    :disabled="!form.include_coupon"
                    v-model="form.active"
                    active-color="#13ce66"
                    inactive-color="#ff4949"
                />
              </el-form-item>
            </div>

            <!-- Coupon Plan group select -->
            <div class="settings-row-block__item">
              <el-form-item label="Plan group"  label-width="125px">
                <el-select
                    size="small"
                    @change="handlePlanGroupChange"
                    v-model="form.plan_group_id"
                    :disabled="!form.include_coupon"
                >
                  <el-option
                      v-for="plan in planGroupsList"
                      :key="plan.value"
                      :label="plan.label"
                      :value="plan.value"
                  />
                </el-select>
              </el-form-item>
            </div>

            <!-- Coupon Plan type select -->
            <div class="settings-row-block__item">
              <el-form-item prop="type" label="Plan type" label-width="125px">
                <el-select
                    size="small"
                    v-model="form.plan_type_id"
                    :disabled="!form.include_coupon"
                >
                  <el-option
                      v-for="type in planTypesByGroup"
                      :key="type.value"
                      :label="type.label"
                      :value="type.value"
                  />
                </el-select>
              </el-form-item>
            </div>

            <!-- Coupon expired at datepicker -->
            <div class="settings-row-block__item">
              <el-form-item prop="expired_at" label="Expired at" label-width="125px">
                <el-date-picker
                    size="small"
                    v-model="form.expired_at"
                    :picker-options="datePickerOptions"
                    type="date"
                    format="MM-dd-yyyy"
                    value-format="yyyy-MM-dd"
                    placeholder="Select Expired Date"
                />
              </el-form-item>
            </div>

            <!-- Coupon reuse counter -->
            <div class="settings-row-block__item">
              <el-form-item label="Available count" label-width="125px">
                <el-input-number v-model="form.available_count" :min="1" :max="100" :disabled="!form.include_coupon" size="small"/>
              </el-form-item>
            </div>

            <!-- Coupon Discount select -->
            <div class="settings-row-block__item">
              <el-form-item label="Discount" label-width="125px">
                <el-select v-model="form.discount_id" :disabled="!form.include_coupon" style="width: 100px" size="small">
                  <el-option
                      v-for="option in discounSelectList"
                      :key="option.label"
                      :label="option.label"
                      :value="option.value"
                  />
                </el-select>
              </el-form-item>
            </div>

            <!-- Coupon URL input -->
            <div class="settings-row-block__item">
              <el-form-item label="Url" prop="url" label-width="125px">
                <el-input v-model="form.url" class="form-item-input" placeholder="http://example.com" size="small" />
              </el-form-item>
            </div>

            <!-- Coupon URN input -->
            <div class="settings-row-block__item">
              <el-form-item label="Urn" prop="urn" label-width="125px">
                <el-input v-model="form.urn" class="form-item-input" placeholder="/example/" size="small" />
              </el-form-item>
            </div>
          </div>

          <div class="settings-row-block">

            <!-- Coupon link UTM tags switcher -->
            <div class="settings-row-block__item">
              <el-form-item label="UTM Settings" label-width="100px">
                <el-switch
                    size="small"
                    v-model="form.include_utm"
                    active-color="#13ce66"
                    inactive-color="#ff4949"
                />
              </el-form-item>
            </div>

            <!-- utm_funnel input -->
            <div class="settings-row-block__item">
              <el-form-item label="Funnel" label-width="100px">
                <el-input v-model="form.utm.utm_funnel" :disabled="!form.include_utm" class="form-item-input" placeholder="utm_funnel" size="small"/>
              </el-form-item>
            </div>

            <!-- utm_source input -->
            <div class="settings-row-block__item">
              <el-form-item label="Source" label-width="100px">
                <el-input v-model="form.utm.utm_source" :disabled="!form.include_utm" class="form-item-input" placeholder="utm_source" size="small"/>
              </el-form-item>
            </div>

            <!-- utm_medium input -->
            <div class="settings-row-block__item">
              <el-form-item label="Medium" label-width="100px">
                <el-input v-model="form.utm.utm_medium" :disabled="!form.include_utm" class="form-item-input" placeholder="utm_medium" size="small"/>
              </el-form-item>
            </div>

            <!-- utm_campaign input -->
            <div class="settings-row-block__item">
              <el-form-item label="Campaign" label-width="100px">
                <el-input v-model="form.utm.utm_campaign" :disabled="!form.include_utm" class="form-item-input" placeholder="utm_campaign" size="small"/>
              </el-form-item>
            </div>

            <!-- utm_content input -->
            <div class="settings-row-block__item">
              <el-form-item label="Content" label-width="100px">
                <el-input v-model="form.utm.utm_content" :disabled="!form.include_utm" class="form-item-input" placeholder="utm_content" size="small" />
              </el-form-item>

              <!-- utm_term input -->
              <div class="settings-row-block__item">
                <el-form-item label="Term" label-width="100px">
                  <el-input v-model="form.utm.utm_term" :disabled="!form.include_utm" class="form-item-input" placeholder="utm_term" size="small" />
                </el-form-item>
              </div>
            </div>
          </div>
        </el-row>

        <el-row class="submit-row">
          <div class="submit-row-block">
            <div class="submit-row-block__item">
              <el-button
                  :loading="isSubmitLoading"
                  @click="discountUpdateHandler"
                  icon="mdi mdi-auto-fix"
                  type="primary" round plain
                  class="submit-btn"
              >
                Update
              </el-button>
            </div>
          </div>
        </el-row>
      </el-form>
    </div>
  </el-dialog>
</template>

<script>
  import discountCoupons from '@/mixins/discountCoupons'
  import CopyButton from '@/components/custom/CopyButton'

  export default {
    name: 'CouponEditDialog',
    mixins: [discountCoupons],
    components: {
      CopyButton
    },
    props: {
      modalState: {
        type: Boolean,
        default: false,
        required: true
      },
      couponId: {
        type: String,
        required: true
      }
    },
    data() {
      const planTypeRequires = (rule, value, callback) => (this.form.plan_group_id !== null && this.form.plan_type_id === null) ? callback('Coupon plan type is required') : callback()
      const checkUrl = (rule, value, callback) => /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/gm.test(value) ? callback() : callback('Incorrect URL format')

      return {
        validateRules: {
          expired_at: [{ required: true, message: 'Select coupon expired date' }],
          url: [{ required: true, message: 'Url is required' }, { validator: checkUrl }],
          type: [{ validator: planTypeRequires }]
        },
        form: {
          url: '',
          urn: '',
          link: '',
          code: '',
          active: '',
          include_utm: '',
          include_coupon: '',
          available_count: '',
          plan_group_id: '',
          plan_type_id: '',
          expired_at: '',
          discount_id: '',
          _method: 'PATCH',
          utm: {
            utm_funnel: '',
            utm_source: '',
            utm_campaign: '',
            utm_medium: '',
            utm_term: '',
            utm_content: ''
          }
        },
        isSubmitLoading: false
      }
    },
    watch: {
      couponId(id) {
        if (this.filterCouponsListById(this.couponId).plan_group_id !== null) {
          this.$store.dispatch('discountCoupons/getPlanTypesByGroup', Number(this.filterCouponsListById(id).plan_group_id))
        }

        this.form = this.filterCouponsListById(id)
      }
    },
    methods: {
      discountUpdateHandler() {
        this.$refs.updateCouponForm.validate(valid => {
          if (valid) {
            this.isSubmitLoading = true
            this.form.plan_group_id = this.typeConversion('str', this.form.plan_group_id)
            this.form.plan_type_id = this.typeConversion('str', this.form.plan_type_id)

            this.$store.dispatch('discountCoupons/updateCoupon', this.form)
                .then(response => {
                  response.plan_group_id = this.typeConversion('num', response.plan_group_id)
                  response.plan_type_id = this.typeConversion('num', response.plan_type_id)

                  this.form = response

                  this.$store.dispatch('discountCoupons/getCouponsList')

                  this.$notify.success('Coupon updated')
                })
                .catch(error => this.$notify.error({ title: error.message,  message: Object.values(error.errors)[0][0] }))
                .finally(() => this.isSubmitLoading = false)
          } else {
            this.isSubmitLoading = true
            return false
          }
        })
      },
      hideModalHandler() {
        this.$emit('hide-modal')
      }
    },
    created() {
      if (this.filterCouponsListById(this.couponId).plan_group_id !== null) {
        this.$store.dispatch('discountCoupons/getPlanTypesByGroup', Number(this.filterCouponsListById(this.couponId).plan_group_id))
      }

      this.form = this.filterCouponsListById(this.couponId)
    }
  }
</script>
